import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, FileText, DollarSign, Calendar } from 'lucide-react';
import InvoiceAgingReport from '../components/reports/InvoiceAgingReport';
import { useSubscription } from '../contexts/SubscriptionContext';
import UpgradePrompt from '../components/subscription/UpgradePrompt';

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeDetails, setUpgradeDetails] = useState<any>({});
  const [activeTab, setActiveTab] = useState<'aging' | 'revenue' | 'payments'>('aging');

  // Listen for global 402 events
  useEffect(() => {
    const handleUpgradeRequired = (event: any) => {
      setUpgradeDetails(event.detail);
      setShowUpgradePrompt(true);
    };
    window.addEventListener('subscription:upgrade-required', handleUpgradeRequired);
    return () => window.removeEventListener('subscription:upgrade-required', handleUpgradeRequired);
  }, []);

  // Gate access client-side
  useEffect(() => {
    if (!hasFeature('hasReports')) {
      setShowUpgradePrompt(true);
      setUpgradeDetails({
        feature: 'Reports',
        message: 'Reports and analytics are only available on Professional and Enterprise plans.',
        requiredPlan: 'Professional',
      });
    }
  }, [hasFeature]);

  if (!hasFeature('hasReports')) {
    return (
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature={upgradeDetails.feature}
        message={upgradeDetails.message}
        requiredPlan={upgradeDetails.requiredPlan}
      />
    );
  }

  const tabs = [
    {
      id: 'aging' as const,
      name: 'Invoice Aging',
      icon: Calendar,
      description: 'Track outstanding invoices by age'
    },
    {
      id: 'revenue' as const,
      name: 'Revenue',
      icon: DollarSign,
      description: 'Revenue analysis and trends'
    },
    {
      id: 'payments' as const,
      name: 'Payments',
      icon: FileText,
      description: 'Payment tracking and analysis'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BarChart3 className="h-8 w-8 text-gray-900 dark:text-gray-100" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Reports
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Business analytics and insights
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-accent-color text-accent-color'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 inline mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'aging' && <InvoiceAgingReport />}
        {activeTab === 'revenue' && (
          <div className="text-center py-12">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Revenue Report
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Coming soon - Revenue analysis and trends
            </p>
          </div>
        )}
        {activeTab === 'payments' && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Payment Report
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Coming soon - Payment tracking and analysis
            </p>
          </div>
        )}
      </div>

      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature={upgradeDetails.feature}
        message={upgradeDetails.message}
        requiredPlan={upgradeDetails.requiredPlan}
      />
    </div>
  );
};

export default ReportsPage;
